import { queueCartRequest } from "./cart-api";
import { formatDeliveryPropertyValue } from "./quick-add-properties";

// The "delivery time" ship date is counted from the day the line was added, so
// stale lines are re-dated from data-delivery-option whenever the cart opens.

const DELIVERY_PROPERTY_KEY = "delivery time";

let refreshInFlight = null;

function getDeliveryOptions() {
  const options = new Map();

  document.querySelectorAll("[cart-section-id] [data-item-key][data-delivery-option]").forEach((item) => {
    options.set(item.dataset.itemKey, item.dataset.deliveryOption);
  });

  return options;
}

function getStaleLineChanges(cart, options) {
  return cart.items.flatMap((item) => {
    const rawOption = options.get(item.key);
    const properties = item.properties || {};
    const propertyKey = Object.keys(properties).find((key) => key.trim().toLowerCase() === DELIVERY_PROPERTY_KEY);

    if (!rawOption || !propertyKey) return [];

    const value = formatDeliveryPropertyValue(rawOption);

    if (!value || value === properties[propertyKey]) return [];

    // /cart/change.js replaces a line's properties wholesale, so every other
    // property (_delivery, _showroom_data, ...) is passed back unchanged.
    return [{ id: item.key, quantity: item.quantity, properties: { ...properties, [propertyKey]: value } }];
  });
}

async function runRefresh() {
  const options = getDeliveryOptions();

  if (!options.size) return;

  // The read and every re-date run as one step in the cart queue, so they
  // can't interleave with a shopper's own quantity edit or add (whose line
  // keys the read would otherwise see out of date).
  const changed = await queueCartRequest(async () => {
    const cart = await fetch("/cart.js", { headers: { "Content-Type": "application/json" } }).then((response) =>
      response.json(),
    );
    const changes = getStaleLineChanges(cart, options);

    // One at a time: a property change gives the line a new key, but leaves
    // every other line's key alone.
    for (const change of changes) {
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(change),
      }).catch((error) => console.error(error));
    }

    return changes.length > 0;
  });

  if (!changed) return;

  // Re-render every cart surface once, so the new dates and line keys are in the DOM.
  window.updateCart({ updates: {} }, { data: { requestId: ++window.cartContentRequestId } });
}

function refreshDeliveryDates() {
  if (!refreshInFlight) {
    refreshInFlight = runRefresh()
      .catch((error) => console.error(error))
      .finally(() => {
        refreshInFlight = null;
      });
  }

  return refreshInFlight;
}

document.addEventListener("mini-cart:open", refreshDeliveryDates);

// The cart page: on load, and again when it's restored from the back/forward cache.
window.addEventListener("pageshow", () => {
  const hasCartPage = [...document.querySelectorAll("cart-content")].some((cart) => !cart.closest("mini-cart"));

  if (hasCartPage) refreshDeliveryDates();
});
